import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import FleetWebApi from "../Api/FleetWebApi";
import { useEffect } from "react";
import { useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import {
  Grid,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress,
  Container,
  Paper,
  Table,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
  FormControlLabel,
  Switch,
} from "@mui/material";

function WaterMonitor(props) {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState();
  const [isSuccess, setIsSuccess] = useState(false);
  //   Start date should be 7 days prior to now
  const [startDate, setStartDate] = useState(
    new Date() - 24 * 60 * 60 * 1000 * 7
  );
  //   End date is 5 minutes ago to avoid any missing data
  const [endDate, setEndDate] = useState(new Date() - 60 * 1000 * 5);
  const [waterData, setWaterData] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [windowSize, setWindowSize] = useState(1);
  const [graphType, setGraphType] = useState(1);
  const [ratioVal, setRatioVal] = useState(1);
  const [timeDelta, setTimeDelta] = useState(endDate - startDate);
  const [showReboots, setShowReboots] = useState(false);
  const [showMetadata, setShowMetadata] = useState(false);
  
  // Use virtualID from props if available, otherwise fall back to coreID
  const sensorId = props.virtualID || props.coreID;
  
  // Single useEffect for rendering both graphs
  useEffect(() => {
    let element = document.getElementById("waterDataAndMeta");
    while (element.firstChild) {
      element.removeChild(element.firstChild);
    }
    if (waterData !== null) {
      try {
        window.Bokeh.embed.embed_item(waterData, "waterDataAndMeta");
      } catch (error) {
        console.log(error);
        setError(error);
      }
    }
  }, [waterData]);

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      handleLoadPlot();
      handleMetaData();
    }
    return () => {
      // clean the state
      isMounted = false;
      setWaterData(null);
      setMetaData(null);
    };
  }, [sensorId, props.focused, showReboots, showMetadata, props.virtualID]);

  useEffect(() => {
    setTimeDelta(endDate - startDate);
  }, [startDate, endDate]);

  function handleLoadPlot() {
    let isMounted = true;

    if (timeDelta > 0) {
      if (timeDelta < 1209612711 || graphType === 1) {
        setError(null);
        setIsLoading(true);
        setWaterData(null);
        if (props.focused) {
          FleetWebApi.getDeviceWaterData(
            props.coreID, 
            startDate,
            endDate,
            windowSize,
            graphType,
            ratioVal,
            showReboots,
            showMetadata,
            props.virtualID 
          )
            .then((result) => {
              if (isMounted) {
                setWaterData(result);
                setIsLoading(false);
                setIsSuccess(true);
              }
            })
            .catch((error) => {
              console.log(error);
              if (isMounted) {
                setError(error.message);
                setIsLoading(false);
              }
            });
        }
      } else {
        setError(
          "Time periods of greater than two weeks is not available for per-minute data."
        );
      }
    } else {
      setError("Start Date/Time must preceed End Date/Time.");
    }
  }

  function handleMetaData() {
    let isMounted = true;

    setMetaData(null);
    FleetWebApi.getDeviceMetaData(props.coreID, props.device_type)
      .then((result) => {
        if (isMounted) {
          setMetaData(result);
        }
      })
      .catch((error) => {
        if (isMounted) {
          setError(error.message);
        }
      });
  }

  return (
    <Grid item xs={12} md={12} lg={12}>
      <h2>Behaviour Graph</h2>
      <Grid container spacing={2}>
        <Grid item xs={12} md={12} lg={2}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              disableToolbar
              variant="inline"
              format="MM/dd/yyyy"
              margin="normal"
              id="date-picker-inline"
              label="Start Date"
              value={startDate}
              onChange={setStartDate}
              KeyboardButtonProps={{
                "aria-label": "change date",
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={12} lg={2}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <TimePicker
              margin="normal"
              id="time-picker"
              label="Start Time"
              value={startDate}
              onChange={setStartDate}
              KeyboardButtonProps={{
                "aria-label": "change time",
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={12} lg={2}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              disableToolbar
              variant="inline"
              format="MM/dd/yyyy"
              margin="normal"
              id="date-picker-inline"
              label="End Date"
              value={endDate}
              onChange={setEndDate}
              KeyboardButtonProps={{
                "aria-label": "change date",
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={12} lg={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <TimePicker
              margin="normal"
              id="time-picker"
              label="End Time"
              value={endDate}
              onChange={setEndDate}
              KeyboardButtonProps={{
                "aria-label": "change time",
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12} md={12} lg={2}>
          <div className="buttonWrapper" style={{ display: "inline-block" }}>
            <Button
              onClick={() => {
                handleLoadPlot();
              }}
              color="primary"
              variant="contained"
              disabled={isLoading}
              className={isSuccess ? "buttonSuccess" : ""}
            >
              Load Water Monitor
            </Button>
            {isLoading && (
              <CircularProgress size={24} className="buttonProgress" />
            )}
          </div>
        </Grid>

        <Grid item xs={12} md={12} lg={2}>
          <FormControl style={{ minWidth: 100 }}>
            <InputLabel id="average-window-size-label">Window Size</InputLabel>
            <Select
              labelId="average-window-size-label"
              id="average-window-size-select"
              value={windowSize}
              label="Average Window Size"
              onChange={(e) => setWindowSize(e.target.value)}
            >
              <MenuItem value={1}>1</MenuItem>
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={3}>3</MenuItem>
              <MenuItem value={4}>4</MenuItem>
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={40}>40</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={60}>60</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={500}>500</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={12} lg={2}>
          <FormControl style={{ minWidth: 100 }}>
            <InputLabel id="graph-type-label">Graph Type</InputLabel>
            <Select
              labelId="graph-type-label"
              id="graph-type-select"
              value={graphType}
              label="Graph Type"
              onChange={(e) => setGraphType(e.target.value)}
            >
              <MenuItem value={0}>Per Minute</MenuItem>
              <MenuItem value={1}>Per Hour</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={12} lg={2}>
          <FormControl style={{ minWidth: 100 }}>
            <InputLabel id="ratio-val-label">Apply Ratio</InputLabel>
            <Select
              labelId="ratio-val-label"
              id="ratio-val-select"
              value={ratioVal}
              label="Ratio Val"
              onChange={(e) => setRatioVal(e.target.value)}
            >
              <MenuItem value={0.25}>0.25x</MenuItem>
              <MenuItem value={0.5}>0.5x</MenuItem>
              <MenuItem value={1}>None</MenuItem>
              <MenuItem value={2}>2x</MenuItem>
              <MenuItem value={4}>4x</MenuItem>
              <MenuItem value={5}>Inverse Average</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={12} lg={2}>
          <FormControl style={{ minWidth: 100 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showReboots}
                  onChange={(e) => setShowReboots(e.target.checked)}
                  color="primary"
                />
              }
              label="Show Reboots"
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={12} lg={2}>
          <FormControl style={{ minWidth: 100 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showMetadata}
                  onChange={(e) => setShowMetadata(e.target.checked)}
                  color="primary"
                />
              }
              label="Show Metadata"
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} md={12} lg={12}>
          <h3>Water Monitor & Metadata Charts</h3>
          <Container className="full-height">
            <Container maxWidth="lg" className="container">
              {isLoading && <CircularProgress />}
              {error && <p>Error Loading Data: {error}</p>}
              <div id="waterDataAndMeta" className="bk-root"></div>
            </Container>
          </Container>
        </Grid>
      </Grid>
      <h2>Latest Device Data</h2>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper>
            <Table classes={{ root: "reactivetable" }}>
              <TableHead>
                <TableRow>
                  <TableCell>Data Type</TableCell>
                  <TableCell>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {metaData
                  ? Object.keys(metaData).map((row, key) => (
                      <TableRow key={key}>
                        <TableCell data-th="Data Type">{row}</TableCell>
                        <TableCell data-th="Value">{metaData[row]}</TableCell>
                      </TableRow>
                    ))
                  : null}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  );
}

export default WaterMonitor;
